import type { FC, ReactNode } from 'react'

import { useId } from 'react'
import { cn } from '@/lib/utils'

export type TextureVariant = 'cream' | 'pearl' | 'amber' | 'sky' | 'apricot' | 'glass'

interface Palette {
    base: string
    colors: string[]
    // Width of one cream band, as a share of the surface.
    period: number
    // How far the bands are pulled into swirls.
    warp: number
    frequency: string
    blur: number
    gloss: number
    streaks?: boolean
    seed: number
}

const PALETTES: Record<TextureVariant, Palette> = {
    cream: {
        base: '#F6E8DA',
        colors: ['#FFF8F0', '#F1DCC8', '#FFFFFF', '#E8CDB6', '#FBEFE3', '#FFFFFF'],
        period: 0.16,
        warp: 300,
        frequency: '0.0042 0.0065',
        blur: 5,
        gloss: 0.55,
        seed: 4
    },
    pearl: {
        base: '#DCE7D8',
        colors: ['#EEF4EA', '#C8DAC4', '#F8F4E6', '#DFCC98', '#D5E5D4', '#FFFFFF', '#E8DBAE'],
        period: 0.15,
        warp: 320,
        frequency: '0.004 0.006',
        blur: 5,
        gloss: 0.5,
        seed: 11
    },
    amber: {
        base: '#E9A05C',
        colors: ['#F7C48A', '#E48F48', '#FFE0B4', '#D27A35', '#F5B676', '#FFD6A0'],
        period: 0.14,
        warp: 300,
        frequency: '0.0045 0.007',
        blur: 4,
        gloss: 0.6,
        streaks: true,
        seed: 7
    },
    sky: {
        base: '#D3E3F3',
        colors: ['#E8F1FA', '#C2D7EE', '#F7FAFD', '#B4CCE8', '#DCE8F5'],
        period: 0.17,
        warp: 300,
        frequency: '0.0045 0.006',
        blur: 5,
        gloss: 0.45,
        seed: 23
    },
    apricot: {
        base: '#F1C6AA',
        colors: ['#F1C6AA', '#F7D9C5', '#FCE9DB', '#EDBC9C', '#F5D0B8'],
        period: 0.2,
        warp: 360,
        frequency: '0.0035 0.005',
        blur: 7,
        gloss: 0.2,
        seed: 15
    },
    glass: {
        base: '#FBEADF',
        colors: ['#FFF9F3', '#F8E1D1', '#FFFFFF', '#F3D4BF', '#FFF3EA'],
        period: 0.18,
        warp: 320,
        frequency: '0.004 0.006',
        blur: 6,
        gloss: 0.6,
        seed: 9
    }
}

interface CreamTextureProps {
    variant: TextureVariant
    className?: string
}

// A soft, blurred swirl of cream: banded gradients pulled around by low-frequency noise.
const CreamTexture: FC<CreamTextureProps> = ({ variant, className }): ReactNode => {
    const uid = useId().replace(/[^a-zA-Z0-9]/g, '')
    const id = (name: string): string => `cream-${uid}-${name}`
    const url = (name: string): string => `url(#${id(name)})`
    const palette = PALETTES[variant]

    return (
        <svg
            viewBox='0 0 600 600'
            preserveAspectRatio='xMidYMid slice'
            className={cn('pointer-events-none absolute inset-0 block size-full', className)}
            aria-hidden='true'
            focusable='false'
        >
            <defs>
                <linearGradient
                    id={id('bands')}
                    x1='0'
                    y1='0'
                    x2={palette.period}
                    y2={palette.period * 0.6}
                    spreadMethod='reflect'
                >
                    {palette.colors.map((color, index) => (
                        <stop key={`${color}-${index}`} offset={index / (palette.colors.length - 1)} stopColor={color} />
                    ))}
                </linearGradient>

                <filter id={id('swirl')} colorInterpolationFilters='sRGB'>
                    <feTurbulence type='fractalNoise' baseFrequency={palette.frequency} numOctaves='2' seed={palette.seed} result='noise' />
                    <feDisplacementMap in='SourceGraphic' in2='noise' scale={palette.warp} xChannelSelector='R' yChannelSelector='G' result='warped' />
                    <feGaussianBlur in='warped' stdDeviation={palette.blur} />
                </filter>

                <radialGradient id={id('gloss')} cx='0.28' cy='0.22' r='0.62'>
                    <stop offset='0' stopColor='#FFFFFF' stopOpacity={palette.gloss} />
                    <stop offset='1' stopColor='#FFFFFF' stopOpacity='0' />
                </radialGradient>

                <filter id={id('streak')} x='-20%' y='-40%' width='140%' height='180%'>
                    <feGaussianBlur stdDeviation='7' />
                </filter>
            </defs>

            <rect width='600' height='600' fill={palette.base} />
            <rect x='-200' y='-200' width='1000' height='1000' fill={url('bands')} filter={url('swirl')} />
            <rect width='600' height='600' fill={url('gloss')} />

            {palette.streaks && (
                <g fill='none' stroke='#FFFFFF' strokeLinecap='round' filter={url('streak')}>
                    <path d='M40 430 C170 300 330 370 580 170' strokeOpacity='0.5' strokeWidth='12' />
                    <path d='M20 540 C210 430 390 480 610 330' strokeOpacity='0.28' strokeWidth='26' />
                    <path d='M110 120 C200 70 300 90 380 60' strokeOpacity='0.4' strokeWidth='8' />
                </g>
            )}
        </svg>
    )
}

export default CreamTexture
